import { Injectable } from '@angular/core';

import { Feature, Point } from 'geojson';
import {
  DistributionPointProperties,
  PointType,
} from '../../../span-progress-entities';

@Injectable({
  providedIn: 'root',
})
export class ClusterPointService {
  markerClusterGroup: {
    [k: string]: Feature<Point, DistributionPointProperties>[];
  };

  createMarkerCluster(
    distPointResponse: Feature<Point, DistributionPointProperties>[],
  ) {
    const coordinateRecord: Record<
      string,
      Feature<Point, DistributionPointProperties>[]
    > = {};

    distPointResponse.forEach((obj) => {
      const key =
        obj.geometry.coordinates[0].toString() +
        obj.geometry.coordinates[1].toString();
      if (!coordinateRecord[key]) {
        coordinateRecord[key] = [];
      }
      coordinateRecord[key].push(obj);
    });
    this.buildMarkerClusterGroup(coordinateRecord);
  }

  buildMarkerClusterGroup(
    coordinateRecord: Record<
      string,
      Feature<Point, DistributionPointProperties>[]
    >,
  ) {
    this.markerClusterGroup = Object.fromEntries(
      Object.entries(coordinateRecord).filter(
        ([, value]) =>
          Array.isArray(value) &&
          value.length > 1 &&
          value.every(
            (point) => point.properties.markerTypeId !== PointType.DuctEndpoint,
          ),
      ),
    );
  }

  updateMarkerTypeId(
    arrayA: Feature<Point, DistributionPointProperties>[],
    arrayB: Feature<Point, DistributionPointProperties>[],
    newMarkerTypeId: number,
  ) {
    return arrayA.map((a) => {
      const match = arrayB.find((b) => b.id === a.id);
      return match
        ? {
            ...a,
            properties: { ...a.properties, markerTypeId: newMarkerTypeId },
          }
        : a;
    });
  }

  flatMapCluster(cluster: Feature<Point, DistributionPointProperties>[][]) {
    return cluster.flatMap((markersArray) =>
      markersArray.map((marker) => ({
        ...marker,
      })),
    );
  }

  checkClusterGroup(
    cluster: Feature<Point, DistributionPointProperties>[],
    pointType: PointType,
  ): boolean {
    return cluster.every(
      (point) => point.properties.markerTypeId === pointType,
    );
  }

  createClusterGroups() {
    const clusters = Object.values(this.markerClusterGroup);
    const hpClusters: Feature<Point, DistributionPointProperties>[][] = [];
    const hcClusters: Feature<Point, DistributionPointProperties>[][] = [];
    const houseHoldClusters: Feature<Point, DistributionPointProperties>[][] =
      [];
    const wellClusters: Feature<Point, DistributionPointProperties>[][] = [];

    clusters.forEach((cluster) => {
      if (this.checkClusterGroup(cluster, PointType.Hp)) {
        hpClusters.push(cluster);
      }
      if (this.checkClusterGroup(cluster, PointType.Hc)) {
        hcClusters.push(cluster);
      }
      if (
        cluster.some(
          (point) => point.properties.markerTypeId === PointType.Hp,
        ) &&
        cluster.some((point) => point.properties.markerTypeId === PointType.Hc)
      ) {
        houseHoldClusters.push(cluster);
      }
      if (
        cluster.every(
          (point) =>
            point.properties.markerTypeId === PointType.SlingWell ||
            point.properties.markerTypeId === PointType.CableWell,
        )
      ) {
        wellClusters.push(cluster);
      }
    });

    return { hpClusters, hcClusters, houseHoldClusters, wellClusters };
  }

  initClusters() {
    const { hcClusters, hpClusters, houseHoldClusters, wellClusters } =
      this.createClusterGroups();

    const hpMultiMarkers = this.flatMapCluster(hpClusters);
    const hcMultiMarkers = this.flatMapCluster(hcClusters);
    const houseHoldMultiMarkers = this.flatMapCluster(houseHoldClusters);
    const wellMultiMarkers = this.flatMapCluster(wellClusters);

    return {
      hpMultiMarkers,
      hcMultiMarkers,
      houseHoldMultiMarkers,
      wellMultiMarkers,
    };
  }

  initDistPoints(features: Feature<Point, DistributionPointProperties>[]) {
    const {
      hpMultiMarkers,
      hcMultiMarkers,
      houseHoldMultiMarkers,
      wellMultiMarkers,
    } = this.initClusters();

    const featuresWithMultipleHp = this.updateMarkerTypeId(
      features,
      hpMultiMarkers,
      -2,
    );
    const featuresWithMultipleHpHc = this.updateMarkerTypeId(
      featuresWithMultipleHp,
      hcMultiMarkers,
      -3,
    );

    const featuresWithMultiHouseHolds = this.updateMarkerTypeId(
      featuresWithMultipleHpHc,
      houseHoldMultiMarkers,
      -4,
    );

    const featuresWithMultiWells = this.updateMarkerTypeId(
      featuresWithMultiHouseHolds,
      wellMultiMarkers,
      -5,
    );

    return featuresWithMultiWells;
  }
}
